import BaseComponent from './BaseComponent';
import { gsap } from 'gsap';

export default class FaqModule extends BaseComponent {
	constructor(element) {
		super();

		this.element = element;

		if (!this.element) {
			return;
		}

		this.items = [];
		const faqItems = this.element.querySelectorAll('[data-faq-item]');

		faqItems.forEach((item) => {
			const faqItem = new FaqItem(item);
			this.items.push(faqItem);
		});
	}

	destroy() {
		super.destroy();

		this.items.forEach((item) => {
			item.destroy();
		});
	}
}

class FaqItem {
	constructor(el) {
		this.el = el;

		this.button = this.el.querySelector('[data-faq-item-trigger]');
		this.target = this.el.querySelector('[data-faq-item-content]');

		if (!this.target || !this.button) {
			return;
		}

		this.isOpen = this.el.classList.contains('is-open');
		this.isAnimating = false;

		if (!this.isOpen) {
			this.setFocusable(false);
			gsap.set(this.target, { height: 0, opacity: 0 });
			gsap.set(this.el, { paddingBottom: 0 });
		} else {
			this.setFocusable(true);
			gsap.set(this.target, { height: 'auto', opacity: 1 });
			gsap.set(this.el, { paddingBottom: 24 });
		}

		this.button.addEventListener('click', this.toggle.bind(this));
		this.button.setAttribute('aria-expanded', this.isOpen);
	}

	toggle() {
		if (this.isAnimating) {
			return;
		}

		this.isOpen ? this.close() : this.open();
	}

	open() {
		if (this.isOpen) {
			return;
		}

		this.isAnimating = true;
		this.el.classList.add('is-open');
		this.button.setAttribute('aria-expanded', 'true');

		this.setFocusable(true);

		return new Promise((resolve) => {
			const height = this.target.scrollHeight;

			const tl = gsap.timeline({
				onComplete: () => {
					gsap.set(this.target, {
						height: 'auto',
					});

					this.isOpen = true;
					this.isAnimating = false;

					resolve();
				},
			});

			tl.to(this.target, {
				height: height,
				duration: 0.3,
				ease: 'power2.out',
			});
			tl.to(
				this.target,
				{
					opacity: 1,
					duration: 0.1,
					ease: 'none',
				},
				'<0.2'
			);
			tl.to(
				this.el,
				{
					paddingBottom: 24,
					duration: 0.3,
					ease: 'power2.out',
				},
				'<-0.3'
			);
		});
	}

	close() {
		if (!this.isOpen) {
			return;
		}

		this.isAnimating = true;
		this.el.classList.remove('is-open');
		this.button.setAttribute('aria-expanded', 'false');

		this.setFocusable(false);

		return new Promise((resolve) => {
			gsap.set(this.target, {
				height: this.target.scrollHeight,
			});

			const tl = gsap.timeline({
				onComplete: () => {
					this.isOpen = false;
					this.isAnimating = false;

					resolve();
				},
			});

			tl.to(this.target, {
				opacity: 0,
				duration: 0.1,
				ease: 'none',
			});
			tl.to(
				this.target,
				{
					height: 0,
					duration: 0.3,
					ease: 'power2.in',
				},
				'<0.1'
			);
			tl.to(
				this.el,
				{
					paddingBottom: 0,
					duration: 0.3,
					ease: 'power2.in',
				},
				'<-0.2'
			);
		});
	}

	setFocusable(enabled) {
		const focusables = this.target.querySelectorAll(
			'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]'
		);

		focusables.forEach((el) => {
			if (enabled) {
				el.removeAttribute('tabindex');
			} else {
				el.setAttribute('tabindex', '-1');
			}
		});
	}

	destroy() {
		if (this.button) {
			this.button.removeEventListener('click', this.toggle.bind(this));
		}
	}
}
